import { Box, Typography } from '@mui/material'
import {
  PercentileChartCohortBackgroundColor,
  PercentileChartCohortLabelBackgroundColorOutside,
} from 'components/ReportElement'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { HtmlTooltip } from './Bar'
import getPercentileLabel from 'util/getPercentilLabel'

export type PercentileCohortData = {
  max: number
  median: number
  min: number
  name: string
}

interface Props {
  data: PercentileCohortData
  size: number
  hideLabel?: boolean
}

const PercentileChartElementCohort: React.FC<Props> = ({
  data,
  size,
  hideLabel = false,
}) => {
  const containerRef = useRef<HTMLElement>()
  const minRef = useRef<HTMLElement>()
  const barRef = useRef<HTMLElement>()
  const labelRef = useRef<HTMLElement>()
  const middleRef = useRef<HTMLElement>()

  const [leftInside, setLeftInside] = useState<number | undefined>()
  const [rightInside, setRightInside] = useState<number | undefined>()
  const [leftOutside, setLeftOutside] = useState<number | undefined>()

  useLayoutEffect(() => {
    if (containerRef.current) {
      const container = containerRef.current.getBoundingClientRect()
      const minLeft = (data.min / 100) * container.width
      const medianLeft = (data.median / 100) * container.width
      const maxLeft = (data.max / 100) * container.width
      setLeftInside(medianLeft - minLeft)
      setRightInside(maxLeft - medianLeft)
      setLeftOutside(container.width - minLeft)
    }
  }, [containerRef, data])

  useEffect(() => {
    if (
      labelRef.current &&
      barRef.current &&
      leftInside != undefined &&
      rightInside != undefined &&
      leftOutside != undefined
    ) {
      const labelRect = labelRef.current.getBoundingClientRect()
      const barRect = barRef.current.getBoundingClientRect()
      const padding = 6
      const horizontalBarWidth = 2
      if (leftInside > labelRect.width) {
        labelRef.current.style.backgroundColor =
          PercentileChartCohortBackgroundColor
        labelRef.current.style.marginLeft = `${horizontalBarWidth + padding}px`
      } else if (rightInside > labelRect.width) {
        labelRef.current.style.backgroundColor =
          PercentileChartCohortBackgroundColor
        labelRef.current.style.marginLeft = `${
          leftInside + horizontalBarWidth + padding
        }px`
      } else if (leftOutside > labelRect.width) {
        labelRef.current.style.backgroundColor =
          PercentileChartCohortLabelBackgroundColorOutside
        labelRef.current.style.marginLeft = `-${labelRect.width + padding}px`
      } else {
        labelRef.current.style.backgroundColor =
          PercentileChartCohortLabelBackgroundColorOutside
        labelRef.current.style.marginLeft = `${barRect.width + padding}px`
      }
    }
  }, [leftInside, rightInside, leftOutside])

  const containerSx = {
    width: '100%',
    height: `${size + 2}px`,
    backgroundColor: '#FFFFFF',
    position: 'relative',
    border: '1px solid #FFFFFF',
  }

  const barSx = {
    backgroundColor: PercentileChartCohortBackgroundColor,
    position: 'absolute',
    top: '0',
    bottom: '0',
    left: `${data.min}%`,
    width: `${data.max - data.min}%`,
    transition: 'all 1000ms',
  }

  const horizontalSx = {
    height: '2px',
    backgroundColor: '#BDBDBD',
    position: 'absolute',
    top: `${size / 2}px`,
    left: `${data.min}%`,
    width: `${data.max - data.min}%`,
    transition: 'all 1000ms',
  }

  const minSx = {
    height: `${size}px`,
    width: '2px',
    backgroundColor: '#16364F',
    position: 'absolute',
    top: `0`,
    left: `${data.min}%`,
    transition: 'all 1000ms',
  }

  const medianSx = {
    height: `${size}px`,
    width: '2px',
    backgroundColor: '#16364F',
    position: 'absolute',
    top: `0`,
    left: `${data.median}%`,
    transition: 'all 1000ms',
  }

  const maxSx = {
    height: `${size}px`,
    width: '2px',
    backgroundColor: '#16364F',
    position: 'absolute',
    top: `0`,
    left: `${data.max}%`,
    transition: 'all 1000ms',
  }

  const labelSx = {
    position: 'absolute',
    left: `${data.min}%`,
    top: `${(size - 14) / 2}px`,
    padding: '0px 4px',
    backgroundColor: PercentileChartCohortBackgroundColor,
    fontSize: '14px',
    lineHeight: '14px',
    transition: 'all 1000ms',
  }

  return (
    <HtmlTooltip
      arrow
      placement="top"
      followCursor={true}
      title={
        <React.Fragment>
          {!hideLabel && data.name && (
            <Typography align="center" fontSize="0.8rem">
              {data.name}
            </Typography>
          )}
          <Typography>{`Min: ${getPercentileLabel(
            Math.round(data.min)
          )}`}</Typography>
          <Typography>{`Median: ${getPercentileLabel(
            Math.round(data.median)
          )}`}</Typography>
          <Typography>{`Max: ${getPercentileLabel(
            Math.round(data.max)
          )}`}</Typography>
        </React.Fragment>
      }
    >
      <Box ref={containerRef} sx={containerSx}>
        <Box ref={barRef} sx={barSx}></Box>
        <Box sx={horizontalSx}></Box>
        <Box ref={minRef} sx={minSx}></Box>
        <Box ref={middleRef} sx={medianSx}></Box>
        <Box sx={maxSx}></Box>
        <Box ref={labelRef} sx={labelSx}>
          {!hideLabel && data.name}
        </Box>
      </Box>
    </HtmlTooltip>
  )
}

export default PercentileChartElementCohort
